<template>
	<div v-if="!isLoading">
		<form v-if="value && !isInputDisabled">
			<LicensePlateInput
				ref="LicensePlateInput"
				v-model="value.licensePlate"
				:display="display"
				:required="isLicensePlateRequired"
				@input="updateValue()"
				:disabled="isInputDisabled"
			/>

			<div v-if="value.checksum && value.checksum !== getVINChecksum" class="text-red-700 font-bold py-4 text-left">
				{{ $t('vin_or_checksum_not_correct') }}
			</div>
			<div v-if="!rentalCar" class="flex flex-wrap">
				<div class="flex flex-col w-1/2 pr-0 md:pr-4 mb-4">
					<BaseInput
						ref="BaseInput.value.valueIdentificationNumber"
						v-model="value.vehicleIdentificationNumber"
						:required="isFINRequired"
						class="w-full mb-0"
						:error-message="$t('enter_valid_vin')"
						:label="$t('vin')"
						mask="FFFFFFFFFFFFFFFFF"
						maxlength="17"
						:placeholder="$t('vin')"
						type="text"
						validation="[0-9a-zA-Z]{0,}"
						@input="updateVehicleIdentificationNumber"
						:disabled="isInputDisabled"
					/>
					<div class="flex justify-between text-sm">
						<div v-if="getVINChecksum">
							{{ $t('check_sum_input') }} <b>{{ getVINChecksum }}</b>
						</div>
						<div v-if="value.checksum">{{ $t('check_sum_from_scan_input') }} {{ value.checksum }}</div>
						<div v-if="vinShorterMax">{{ $t('vin_shorter_max') }}</div>
					</div>
				</div>
				<AutocompleteInput
					ref="BaseInput.value.manufacturer"
					v-model="value.manufacturer"
					:required="isVehicleDetailsRequired"
					class="w-1/2"
					:label="$t('vehicle_manufacturer')"
					mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
					:placeholder="$t('vehicle_manufacturer')"
					type="text"
					@input="updateManufacturer"
					@selectValue="selectManufacturer"
					:disabled="isInputDisabled"
					:options="manufacturerList"
					@blur="manufacturerList = null"
				/>
			</div>

			<div v-if="!rentalCar" class="flex flex-wrap">
				<AutocompleteInput
					ref="BaseInput.value.model"
					v-model="value.model"
					:required="isVehicleDetailsRequired"
					class="w-1/2 pr-0 md:pr-4"
					:label="$t('vehicle_model')"
					mask="BXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
					:placeholder="$t('vehicle_model')"
					type="text"
					@input="updateModel"
					@click="updateModel('')"
					@selectValue="selectModel"
					:disabled="isInputDisabled"
					:options="modelList"
					@blur="modelList = null"
				/>

				<BaseSelect
					ref="anzahlVorbesitzer"
					v-model="prevOwner"
					:options="previousOwnerOptions"
					:required="isVehicleDetailsRequired"
					:label="$t('previous_owner_amount')"
					@input="updateValue()"
					:disabled="isInputDisabled"
				/>
			</div>

			<div v-if="rentalCar" class="flex flex-wrap">
				<BaseInput
					ref="BaseInput.value.manufacturer"
					v-model="value.manufacturer"
					:required="true"
					class="w-1/2 pr-0 md:pr-4"
					:label="$t('vehicle_manufacturer')"
					mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
					:placeholder="$t('vehicle_manufacturer')"
					type="text"
					@input="updateManufacturer"
				/>
				<BaseInput
					ref="BaseInput.value.model"
					v-model="value.model"
					:required="true"
					class="w-1/2"
					:label="$t('vehicle_model')"
					mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
					:placeholder="$t('vehicle_model')"
					type="text"
					@input="updateValue()"
				/>
			</div>
			<div class="flex">
				<div v-if="getAuthenticatedUserGroupLevel === '2' && !showExtendedVehicleDetails" class="flex w-full">
					<div class="xl:w-1/2 xl:mr-4 md:pr-4">
						<BaseSelect
							v-model="selectedRentalCarClass"
							:options="rentalCarClasses"
							class="flex w-3/4"
							:label="$t('rentalcar_class')"
							@input="updateValue()"
						/>
					</div>
				</div>
			</div>
			<div v-if="showExtendedVehicleDetails" class="flex flex-wrap">
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseDatepicker
						ref="BaseInput.value.registrationFirst"
						:required="true"
						:label="$t('registration_first')"
						placeholder="01.01.2021"
						type="text"
						error-behavior="blur"
						v-model="registrationFirst"
						@input="updateRegistrationFirst"
						class="md:mr-4 w-full md:w-3/4"
						:max_date="new Date()"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseDatepicker
						ref="BaseInput.value.registrationLast"
						:required="true"
						:label="$t('registration_last')"
						placeholder="01.01.2021"
						type="text"
						error-behavior="blur"
						@input="updateRegistrationLast"
						v-model="registrationLast"
						class="md:mr-4 w-full md:w-3/4"
						:max_date="new Date()"
						:min_date="registrationFirst"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<YearMonthInput
						class="md:mr-4 w-full md:w-3/4"
						ref="BaseInput.value.huMonth"
						:required="false"
						:label="$t('hu')"
						@input="updateHuValue"
						:value="huValue"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<YearMonthInput
						class="md:mr-4 w-full md:w-3/4"
						ref="BaseInput.value.auMonth"
						:required="false"
						:label="$t('au')"
						@input="updateAuValue"
						:value="auValue"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseSelect
						:required="false"
						:label="$t('fuel')"
						:options="fuelOptions"
						v-model="selectedFuelOption"
						ref="BaseSelect.value.fuel"
						:allow-empty="false"
						class="w-3/4 mr-4"
						@input="updateFuel"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseSelect
						:required="false"
						:label="$t('exhaust')"
						:options="exhaustOptions"
						v-model="selectedExhaustOption"
						ref="BaseSelect.value.exhaustKey"
						:allow-empty="false"
						class="w-3/4 mr-4"
						@input="updateExhaust"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseInputKilometrage
						v-if="
							showExtendedVehicleDetails &&
								value.kilometrageChanges !== undefined &&
								value.kilometrageChanges !== null &&
								value.kilometrageChanges.length > 0
						"
						ref="BaseInput.value.kilometrage"
						:required="false"
						:label="$t('mileage')"
						type="tel"
						class="w-3/4"
						base-input-class="mb-0"
						v-model="value.kilometrage"
						:last-value="value.kilometrageChanges.length > 0 && true ? value.kilometrageChanges[0] : null"
						@input="updateKilometrageNumber"
						@revision="this.validationMode === 'damageAssessorCase' ? openRevision : null"
						:min-number="value.kilometrageChanges.length > 0 && true ? value.kilometrageChanges[0].kilometrage : 0"
						:error-message="$t('km_smaller_one_hint')"
						:max-length="6"
					/>
					<BaseInputNumber
						v-else
						ref="BaseInput.value.kilometrage"
						:required="false"
						:label="$t('mileage')"
						type="tel"
						class="w-3/4"
						base-input-class="mb-0"
						v-model="value.kilometrage"
						@input="updateValue()"
						:error-message="$t('km_smaller_one_hint')"
						:max-length="6"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4">
					<BaseSelect
						:required="false"
						:label="$t('km_miles')"
						:options="selectedKmMiOptions"
						v-model="selectedKmMiOption"
						ref="BaseSelect.value.kmMiKey"
						:allow-empty="true"
						class="w-3/4 mr-4"
						@input="updateKmMiKey"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4 mb-4">
					<BaseInputNumber
						ref="BaseInput.value.kw"
						:required="false"
						:label="$t('kw')"
						type="tel"
						class="w-3/4"
						base-input-class="mb-0"
						v-model="value.kw"
						@input="updateValue()"
						:error-message="$t('km_smaller_one')"
						:max-length="3"
					/>
				</div>
				<div class="flex flex-col w-1/2 pr-0 md:pr-4 mb-4">
					<BaseInputNumber
						ref="BaseInput.value.displacement"
						:required="false"
						:label="$t('displacement')"
						type="tel"
						class="w-3/4"
						@input="updateValue()"
						base-input-class="mb-0"
						v-model="value.displacement"
						:error-message="$t('displacement_smaller_one')"
						:max-length="4"
					/>
				</div>
				<div class="flex flex-col w-full pr-0 md:pr-4">
					<BaseInput v-model="value.note" :label="$t('notes')" ref="BaseInput.value.note">
						<textarea v-model="value.note" @input="updateValue()" />
					</BaseInput>
				</div>
			</div>
			<router-view />
		</form>
	</div>
	<LoadingSpinner v-else />
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { getOptionFromOptionsArray, getStringFromDate, getYesNoOptions } from '@/plugins/Utils';
import LicensePlateInput from '@/components/forms/inputs/LicensePlateInput';
import Validate from '@/mixins/Validate';
import Authentication from '@/mixins/Authentication';
import { VehiclesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber';
import VehicleRepository from '@/api/repositories/VehicleRepository';
import AutocompleteInput from '@/components/forms/inputs/AutocompleteInput';
import YearMonthInput from '@/components/forms/inputs/YearMonthInput';
import BaseInputKilometrage from '@/components/forms/inputs/BaseInputKilometrage.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
export default {
	components: {
		LoadingSpinner,
		BaseInputKilometrage,
		YearMonthInput,
		AutocompleteInput,
		BaseInputNumber,
		BaseDatepicker,
		LicensePlateInput,
		BaseInput,
		BaseSelect,
	},
	mixins: [Validate, Authentication],
	props: {
		value: {},
		rentalCar: { default: false, type: Boolean },
		showCarClassSelect: { default: false, type: Boolean },
		display: {},
		isCustomerCar: { default: false, type: Boolean },
		isCustomerCarEdit: { default: false, type: Boolean },
		isInputDisabled: { default: false, type: Boolean },
		validationMode: {
			type: String,
			default: 'default',
		},
	},
	computed: {
		selectedKmMiOptions() {
			return [
				{ display: this.$t('kilometer'), value: 'km' },
				{ display: this.$t('miles'), value: 'mi' },
			];
		},
		getVINChecksum() {
			const stringValue = this.value?.vehicleIdentificationNumber?.toString();
			const isValidLength = stringValue && stringValue.length === 17;
			let checksum;
			let sum = 0;
			let weight = 9;

			if (!isValidLength) {
				return '';
			}

			// Summiere die Werte der einzelnen Zeichen
			for (let i = 0; i < stringValue.length; i++) {
				const char = stringValue[i];
				const charValue = this.getEbcdic[char.toLowerCase()] || Number.parseInt(char);

				sum += charValue * weight;
				//console.log("Zeichen: " + char, "Zeichenwert: " + charValue, "Gewicht: " + weight, "Produkt: " + charValue * weight)
				weight--;
				if (weight === 1) {
					weight = 10;
				}
			}
			//console.log("Summe: " + sum)
			checksum = sum % 11;

			// Der Rest der Modulo Division ist der Prüfsumme. Wenn es 10 ist, muss es ein X werden.
			if (checksum === 10) {
				checksum = 'X';
			}

			return '' + checksum;
		},
		vinShorterMax() {
			if (this.value === undefined) {
				return false;
			}
			return this.value?.vehicleIdentificationNumber?.toString().length < 17;
		},
		isFINRequired() {
			if (this.validationMode === 'damageAssessorCase') {
				return false;
			}
			return true;
			//return this.getAuthenticatedUserGroupLevel === '1' && this.isAuthenticatedUserInGroupMitarbeiter;
		},
		getEbcdic() {
			return {
				a: 1,
				b: 2,
				c: 3,
				d: 4,
				e: 5,
				f: 6,
				g: 7,
				h: 8,
				i: 9,
				j: 1,
				k: 2,
				l: 3,
				m: 4,
				n: 5,
				o: 6,
				p: 7,
				q: 8,
				r: 9,
				s: 2,
				t: 3,
				u: 4,
				v: 5,
				w: 6,
				x: 7,
				y: 8,
				z: 9,
			};
		},
		isLicensePlateRequired() {
			return true;
		},
		isVehicleDetailsRequired() {
			return this.validationMode !== 'damageAssessorCase';
		},
		showExtendedVehicleDetails() {
			return this.isCustomerCar || this.validationMode === 'damageAssessorCase';
		},
	},
	data() {
		return {
			updateVehicleIdentificationNumberTimeoutId: null,
			updateModelTimeoutId: null,
			updateManufacturerTimeoutId: null,
			manufacturerList: [],
			openManufacturerSelect: false,
			selectedManufacturer: null,
			selectedModel: null,
			modelList: [],
			yesNoOptions: getYesNoOptions(),
			init: false,
			addNewCustomerCar: false,
			selectedCustomCar: null,
			selectedRentalCarClass: null,
			startKilometrage: null,
			kilometrageSelect: null,
			kilometrageOptions: [],
			oldWmi: '',
			isLoading: true,
			rentalCarClasses: [
				{ display: 'Bitte wählen...', value: null },
				{ display: '1', value: 1 },
				{ display: '2', value: 2 },
				{ display: '3', value: 3 },
				{ display: '4', value: 4 },
				{ display: '5', value: 5 },
				{ display: '6', value: 6 },
				{ display: '7', value: 7 },
				{ display: '8', value: 8 },
				{ display: '9', value: 9 },
				{ display: '10', value: 10 },
			],
			previousOwnerOptions: [
				{ display: '0', value: 0 },
				{ display: '1', value: 1 },
				{ display: '2', value: 2 },
				{ display: '3', value: 3 },
				{ display: '4', value: 4 },
				{ display: '5', value: 5 },
				{ display: '6', value: 6 },
				{ display: '7', value: 7 },
				{ display: '8', value: 8 },
				{ display: '9', value: 9 },
			],
			prevOwner: null,
			fuelOptions: [
				{ display: 'Diesel', value: 'Diesel' },
				{ display: 'Benzin', value: 'Benzin' },
				{ display: 'Strom', value: 'Strom' },
				{ display: 'Hybrid', value: 'Hybrid' },
				{ display: 'Benzin LPG', value: 'Benzin LPG' },
				{ display: 'Wasserstoff', value: 'Wasserstoff' },
				{ display: 'Plug-in Hybrid', value: 'Plug-in Hybrid' },
			],
			exhaustOptions: [
				{ display: 'Euro2', value: 'Euro2' },
				{ display: 'Euro3', value: 'Euro3' },
				{ display: 'Euro4', value: 'Euro4' },
				{ display: 'Euro5', value: 'Euro5' },
				{ display: 'Euro6', value: 'Euro6' },
				{ display: 'Euro6dtemp', value: 'Euro6dtemp' },
				{ display: 'Elektro', value: 'Elektro' },
			],
			selectedFuelOption: null,
			selectedKmMiOption: null,
			selectedExhaustOption: null,
			auValue: null,
			huValue: null,
			registrationFirst: null,
			registrationLast: null,
		};
	},
	mounted() {
		if (this.isCustomerCarEdit) {
			this.isLoading = true;
		} else {
			this.isLoading = false;
		}
		if (this.value?.vehicleIdentificationNumber) {
			this.updateVehicleIdentificationNumber(this.value?.vehicleIdentificationNumber?.toString());
		}
	},
	methods: {
		openRevision() {
			this.$store.commit('setMuteConfirmLeaving', true);
			this.$router.push({ name: 'management.customer.vehicles.edit.kilometrageChange' });
		},
		updateAuValue(value) {
			const registrationLast = this.registrationLast ? getStringFromDate(this.registrationLast, ({ month, year }) => `${year}-${month}`) : null;
			this.auValue = value;

			if (value && value < registrationLast) {
				this.$swal(this.$t('general.attention').toString(), this.$t('inspection_date_error').toString(), 'warning');
				this.$nextTick(() => {
					this.auValue = registrationLast;
					this.updateHuValue(this.auValue);
				});
			} else {
				this.value.au = this.auValue;
				this.updateValue();
			}
		},
		updateHuValue(value) {
			this.huValue = value;
			this.value.hu = this.huValue;

			if (value !== '') {
				this.updateAuValue(value);
			}

			this.updateValue();
		},
		selectManufacturer(manufacturer) {
			this.selectedManufacturer = manufacturer;
			this.value.manufacturer = manufacturer;
			this.updateValue();
			clearTimeout(this.updateManufacturerTimeoutId);
		},
		selectModel(model) {
			this.selectedModel = model;
			this.value.model = model;

			clearTimeout(this.updateModelTimeoutId);
			this.updateValue();
		},
		updateKmMiKey(option) {
			this.value.kmMiKey = option.value;
			this.updateValue();
		},
		updateFuel(option) {
			if (option.value === 'Strom') {
				this.selectedExhaustOption = { display: 'Elektro', value: 'Elektro' };
				this.value.exhaustKey = this.selectedExhaustOption.value;
				this.exhaustOptions = [{ display: 'Elektro', value: 'Elektro' }];
			} else {
				this.setExhaustOptionsDefault();
				if (this.selectedExhaustOption?.value === 'Elektro') {
					this.selectedExhaustOption = null;
				}
			}
			this.value.fuel = option.value;
			this.updateValue();
		},
		updateKilometrageNumber() {
			if (this.value.kilometrageChanges != null) {
				if (this.value.kilometrage.toString() !== this.value.kilometrageChanges[0].kilometrage.toString()) {
					this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
					this.updateValue();
				}
			} else if (this.value.kilometrage) {
				this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
				this.updateValue();
			}
		},
		updateExhaust(option) {
			if (option.value === 'Elektro') {
				this.selectedKmMiOption = this.fuelOptions.find(option => option.value === 'Strom');
				this.exhaustOptions = [{ display: 'Elektro', value: 'Elektro' }];
			}
			this.value.exhaustKey = this.selectedExhaustOption.value;
			this.updateValue();
		},
		setExhaustOptionsDefault() {
			this.exhaustOptions = [
				{ display: 'Euro2', value: 'Euro2' },
				{ display: 'Euro3', value: 'Euro3' },
				{ display: 'Euro4', value: 'Euro4' },
				{ display: 'Euro5', value: 'Euro5' },
				{ display: 'Euro6', value: 'Euro6' },
				{ display: 'Euro6dtemp', value: 'Euro6dtemp' },
				{ display: 'Elektro', value: 'Elektro' },
			];
		},
		updateVehicleIdentificationNumber(value) {
			if (this.value.vehicleIdentificationNumber.includes('O')) {
				this.value.vehicleIdentificationNumber = this.value.vehicleIdentificationNumber.replace('O', '0');
			}
			const wmi = value.substring(0, 3);
			const wmivds = value.substring(0, 9);
			//const isValid = this.$refs['BaseInput.value.valueIdentificationNumber'].isValueValid(value);
			this.value.checksum = null;

			if (wmi !== this.oldWmi && wmi !== '') {
				clearTimeout(this.updateVehicleIdentificationNumberTimeoutId);
				this.updateVehicleIdentificationNumberTimeoutId = setTimeout(() => {
					VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.vehiclesVindataWmivdsGet(wmivds)
						.then(response => {
							this.$set(this.value, 'manufacturer', response.data?.manufacturer?.name);
							this.updateValue();
							if (response.data.is_checked) {
								this.value.model = response.data.model;
							}
						});
				}, 300);
			} else {
				this.updateValue();
			}
			this.oldWmi = wmi;
		},
		updateRegistrationFirst(value) {
			this.value.registrationFirst = getStringFromDate(value, ({ day, month, year }) => [year, month, day].join('-'));

			if (this.prevOwner.value === 0) {
				if (!this.registrationLast || this.registrationLast < this.registrationFirst) {
					if (this.registrationLast && this.registrationLast < this.registrationFirst) {
						this.$swal(this.$t('general.attention').toString(), this.$t('last_registration_updated').toString(), 'warning');
					}
					this.registrationLast = value;
					this.updateRegistrationLast(value);
				} else {
					this.updateValue();
				}
			} else {
				this.updateValue();
			}
		},
		updateRegistrationLast(value) {
			if (this.registrationLast < this.registrationFirst) {
				this.$swal(this.$t('general.attention').toString(), this.$t('last_before_first_registration_error').toString(), 'warning');
				// without next tick the text changes will not be applied to DateInput
				this.$nextTick(() => {
					this.registrationLast = this.registrationFirst;
					this.updateRegistrationLast(this.registrationLast);
				});
			} else {
				const yearMonthRegistrationLast = getStringFromDate(this.registrationLast, ({ month, year }) => `${year}-${month}`);
				this.value.registrationLast = getStringFromDate(value, ({ day, month, year }) => [year, month, day].join('-'));

				if (!this.value.hu || this.value.hu < yearMonthRegistrationLast) {
					if (this.value.hu) {
						this.updateHuValue(this.huValue); // trigger cascading validation if huValue is already set
					} else {
						// set huValue and auValue based on registrationLast if huValue is not set
						this.updateHuValue(yearMonthRegistrationLast);
					}
				}

				this.updateValue();
			}
		},
		updateValue: function() {
			this.value.numberOfPreviousOwners = this.prevOwner?.value;
			if (this.selectedRentalCarClass === null) {
				this.value.rentalCarClass = null;
			} else {
				this.value.rentalCarClass = this.selectedRentalCarClass?.value;
			}

			this.$emit('updateValue', this.value);
		},
		updateManufacturer(value) {
			if (value === this.selectedManufacturer) return;
			this.selectedManufacturer = null;
			clearTimeout(this.updateManufacturerTimeoutId);
			this.updateManufacturerTimeoutId = setTimeout(() => {
				new VehicleRepository().getManufacturerNames(value).then(response => {
					this.manufacturerList = response.data?.map(manufacturer => manufacturer.name);
					this.updateValue();
				});
			}, 300);
		},
		updateModel(value) {
			this.modelList = null;
			if (value === this.selectedModel) return;
			clearTimeout(this.updateModelTimeoutId);
			this.updateModelTimeoutId = setTimeout(() => {
				new VehicleRepository().getModelNames(value, this.value.manufacturer).then(response => {
					this.modelList = response.data?.map(model => model.name);
				});
			}, 300);
			this.updateValue();
		},
	},
	watch: {
		value: {
			deep: true,
			immediate: true,
			handler() {
				if (this.isCustomerCarEdit && this.value !== undefined) {
					this.isLoading = false;
				}
				const valueKeys = Object.keys({ ...this.value }); // check if value is empty
				if (!this.init && valueKeys.length) {
					this.selectedRentalCarClass = getOptionFromOptionsArray(this.rentalCarClasses, this.value.rentalCarClass);
					if (this.value?.numberOfPreviousOwners) {
						this.prevOwner = { display: this.value?.numberOfPreviousOwners, value: this.value?.numberOfPreviousOwners };
					}

					if (this.isCustomerCarEdit && this.value?.id) {
						new VehicleRepository().getVehicleAllKilometrage(this.value.id).then(response => {
							const kmageOptions = [];
							for (let i = 0; i < response.data.length; i++) {
								kmageOptions.push({ display: response.data[i].km + ' (' + response.data[i].date + ')', value: response.data[i].km });
							}
							this.kilometrageOptions = kmageOptions;
							this.kilometrageSelect = this.kilometrageOptions[0] ?? null;
						});
					}

					if (this.value?.exhaustKey) {
						this.selectedExhaustOption = this.exhaustOptions.find(option => option.value === this.value?.exhaustKey);
					}

					this.value.kmMiKey = this.value.kmMiKey || 'km'; // set default to km
					this.selectedKmMiOption = this.selectedKmMiOptions.find(option => option.value === this.value.kmMiKey);

					if (this.value?.fuel) {
						this.selectedFuelOption = this.fuelOptions.find(option => option.value === this.value.fuel);
					}

					this.auValue = this.value.au;
					this.huValue = this.value.hu;
					this.registrationFirst = this.value.registrationFirst ? new Date(this.value.registrationFirst) : null;
					this.registrationLast = this.value.registrationLast ? new Date(this.value.registrationLast) : null;

					// this.kilometrage = this.value.kilometrage;
					// this.kw = this.value.kw;
					// this.displacement = this.value.displacement
					this.init = true;
				}
			},
		},
		'value.numberOfPreviousOwners': {
			handler: function(after, before) {
				this.prevOwner = after || after === 0 ? { display: after, value: after } : null;
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
<style>
.vue-monthly-picker .placeholder {
	color: #8b8b8b !important;
}
</style>
